import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const dealStages = [
  { value: "initial_contact", label: "Første kontakt", order: 1 },
  { value: "qualification", label: "Kvalifisering", order: 2 },
  { value: "pitch", label: "Pitch", order: 3 },
  { value: "due_diligence", label: "Due Diligence", order: 4 },
  { value: "negotiation", label: "Forhandling", order: 5 },
  { value: "closing", label: "Avslutning", order: 6 },
  { value: "closed_won", label: "Vunnet", order: 7 },
  { value: "closed_lost", label: "Tapt", order: 7 },
];

export default function DealStageTracker({ investor, onUpdateStage }) {
  const currentStage = investor.deal_stage || "initial_contact";
  const currentStageInfo = dealStages.find(s => s.value === currentStage);
  const currentOrder = currentStageInfo?.order || 1;

  const getStageStatus = (stage) => {
    if (stage.value === currentStage) return "current";
    if (stage.value === "closed_lost" && currentStage === "closed_lost") return "lost";
    if (stage.value === "closed_won" && currentStage === "closed_won") return "won";
    if (stage.order < currentOrder && stage.value !== "closed_lost") return "completed";
    return "upcoming";
  };

  const activeStages = dealStages.filter(s => {
    if (currentStage === "closed_won" || currentStage === "closed_lost") {
      return s.order <= 6 || s.value === currentStage;
    }
    return s.order <= 6;
  });

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Deal Stage</h3>
          <p className="text-sm text-slate-500">Spor fremdriften i salgsprosessen</p>
        </div>
        <Badge className={cn(
          "border-0",
          currentStage === "closed_won" ? "bg-emerald-100 text-emerald-700" :
          currentStage === "closed_lost" ? "bg-red-100 text-red-700" :
          "bg-lime-100 text-zinc-900"
        )}>
          {currentStageInfo?.label}
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-500">Fremdrift</span>
          <span className="text-xs font-semibold text-slate-700">
            {Math.round((currentOrder / 7) * 100)}%
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500",
              currentStage === "closed_won" ? "bg-emerald-500" :
              currentStage === "closed_lost" ? "bg-red-500" :
              "bg-zinc-900"
            )}
            style={{ width: `${(currentOrder / 7) * 100}%` }}
          />
        </div>
      </div>

      {/* Stages */}
      <div className="space-y-3">
        {activeStages.map((stage, index) => {
          const status = getStageStatus(stage);
          const isActive = status === "current";
          const isCompleted = status === "completed" || status === "won";
          const isLost = status === "lost";

          return (
            <div key={stage.value} className="relative">
              <div
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer",
                  isActive && "border-zinc-900 bg-lime-100",
                  isCompleted && "border-emerald-200 bg-emerald-50",
                  isLost && "border-red-200 bg-red-50",
                  !isActive && !isCompleted && !isLost && "border-slate-200 bg-white hover:border-slate-300"
                )}
                onClick={() => onUpdateStage && onUpdateStage(stage.value)}
              >
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                  isCompleted && "bg-emerald-500",
                  isActive && "bg-zinc-900",
                  isLost && "bg-red-500",
                  !isActive && !isCompleted && !isLost && "bg-slate-200"
                )}>
                  {isCompleted || isLost ? (
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  ) : (
                    <Circle className={cn(
                      "w-5 h-5",
                      isActive ? "text-white" : "text-slate-400"
                    )} />
                  )}
                </div>
                <div className="flex-1">
                  <p className={cn(
                    "font-medium",
                    isActive && "text-zinc-900",
                    isCompleted && "text-emerald-900",
                    isLost && "text-red-900",
                    !isActive && !isCompleted && !isLost && "text-slate-600"
                  )}>
                    {stage.label}
                  </p>
                  {isActive && (
                    <p className="text-xs text-zinc-900">Nåværende stage</p>
                  )}
                </div>
                {isActive && onUpdateStage && index < activeStages.length - 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-zinc-900 hover:text-zinc-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateStage(activeStages[index + 1].value);
                    }}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Connector Line */}
              {index < activeStages.length - 1 && (
                <div className="h-4 flex justify-center">
                  <div className={cn(
                    "w-0.5",
                    isCompleted ? "bg-emerald-300" : "bg-slate-200"
                  )} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {currentStage !== "closed_won" && currentStage !== "closed_lost" && (
        <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-3">
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => onUpdateStage && onUpdateStage("closed_won")}
          >
            Marker som vunnet
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50"
            onClick={() => onUpdateStage && onUpdateStage("closed_lost")}
          >
            Marker som tapt
          </Button>
        </div>
      )}
    </Card>
  );
}